import React, { useEffect, useState } from "react";
import { get, omitBy } from "lodash/fp";
import {
  Container,
  Pagination,
  Table,
  Footer,
  Content,
  Button,
  Form,
  Input,
  Col,
  InputGroup,
  SelectPicker,
  Checkbox,
  Header,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import SaveIcon from "@rsuite/icons/AddOutline";
import CloseIcon from "@rsuite/icons/Close";
import { ComposerFields, ComposerListParams } from "./ComposerDataTypes";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import { updateIn } from "json-immut";
import MasterEditCell from "../../components/Elements/MasterEditCell";
import ActionCell from "./ActionCell";
import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import { useTranslation } from "react-i18next";
import { HelperServices } from "../../../core/services/HelperServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { appConstObj, messageTypeArr } from "../../../core/config/AppConstants";
import "./Composers.css";
import { isEmptyVal } from "../../../utils/helpers";
import CheckCell from "../../components/Elements/CheckCell";

function Composers({
  withHeader = true,
  multiSelect = true,
  showCreateForm = true,
  onSelectComposer,
  ...props
}: {
  withHeader?: boolean;
  multiSelect?: boolean;
  showCreateForm?: boolean;
  onSelectComposer?: Function;
}) {
  const { Column } = Table;
  const [loading, setLoading] = useState(false);
  const [composerId, setComposerId] = useState(0);
  const [data, setData] = useState<ComposerFields[]>([]);
  const [editData, setEditData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [page, setPage] = useState(1);
  const [sort_by, setSortBy] = useState();
  const [sort_order, setSortOrder] = useState();
  const [errors, setErrors] = useState<any>({});
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const [openNoComposerModal, setOpenNoComposerModal] = useState(false);
  const { t } = useTranslation();
  const initSearchFormData = {
    name: "",
    status: "",
  };

  const initFormSaveData = {
    composer_name: "",
    status: 1,
  };

  const [formData, setFormData] =
    useState<ComposerListParams>(initSearchFormData);

  const [formSaveData, setFormSaveData] =
    useState<ComposerFields>(initFormSaveData);

  const [showCreate, setShowCreate] = useState<boolean>(showCreateForm);

  const [checkedKeys, setCheckedKeys] = useState<any[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    let keys: any;
    if (!checked) {
      keys = checkedKeys.filter((item) => item !== value);
    } else if (checked && multiSelect) {
      keys = [...checkedKeys, value];
    } else if (checked && !multiSelect) {
      keys = [value];
    }
    setCheckedKeys(keys);
  };

  function setSelectedComposer() {
    if (checkedKeys.length === 0) {
      setOpenNoComposerModal(true);
      return true;
    } else {
      const composerData = data
        .filter((item) => item.id == checkedKeys[0])
        .shift();
      onSelectComposer && onSelectComposer(composerData);
    }
  }

  async function getComposers(params: ComposerListParams) {
    try {
      setLoading(true);
      const res = await HelperServices.getComposers(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      if (!showCreateForm) {
        if (get("data.total", res) <= 0) {
          setShowCreate(UtilityServices.hasPermission("create-song"));
        } else {
          setShowCreate(false);
        }
      }

      setCheckedKeys([]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ name: smartSearchInput },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: ComposerListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    return params;
  }

  const handleEditData = async (id: Number) => {
    let nextData: any = {};
    let error: string = "";
    let activeItem: any = {};
    nextData = Object.assign([], data);
    activeItem = nextData?.find((item: any) => item.id === id);
    activeItem.editStatus = activeItem?.editStatus ? null : "EDIT";

    if (activeItem.editStatus == null) {
      if (isEmptyVal(activeItem.composer_name))
        error = t("REQUIRED", {
          field: t("COMPOSER").toLowerCase() + " " + t("NAME").toLowerCase(),
        });

      if (!isEmptyVal(error)) {
        UtilityServices.showToaster({
          message: error,
          type: messageTypeArr.error,
        });
        activeItem.editStatus = activeItem?.editStatus ? null : "EDIT";
        return;
      }

      let params = {
        composer_name: activeItem.composer_name,
        status: activeItem.status,
      };

      try {
        setLoading(true);
        const response: any = await HelperServices.updateComposer(
          activeItem.id,
          params
        );
        UtilityServices.showToaster({
          message: response?.data?.message,
        });
        setPage(1);
        setFormSaveData(formSaveData);
        getComposers(
          getSearchParams({
            page: page,
            sort_by: sort_by,
            sort_order: sort_order,
            rows: rows,
          })
        );

        setLoading(false);
      } catch (error: any) {
        UtilityServices.showToaster({
          message: error?.message,
          type: messageTypeArr.error,
        });
        setLoading(false);
        activeItem.editStatus = activeItem?.editStatus ? null : "EDIT";
      }
    }

    setEditData(nextData);
  };

  const handleDataChanges = (id: Number, key: any, value: any) => {
    let nextData: any = {};
    nextData = Object.assign([], data);
    nextData.find((item: any) => item.id === id)[key] =
      key == "status" ? (value ? 1 : 0) : value;
    setEditData(nextData);
  };

  const clearSaveForm = () => {
    setErrors({});
    setFormSaveData(initFormSaveData);
  };

  async function addComposer() {
    try {
      let i;
      let data = { ...formSaveData };
      let dumpError = { ...errors };
      for (i in data) {
        if (data[i] === "") {
          dumpError[i] = t("FIELD_REQUIRED");
          setErrors(dumpError);
        } else {
          delete dumpError[i];
          setErrors(dumpError);
        }
      }

      if (Object.keys(dumpError).length == 0) {
        setLoading(true);
        data.composer_name = data.composer_name;
        const response: any = await HelperServices.createComposer(data);
        UtilityServices.showToaster({
          message: response?.data?.message,
        });

        setPage(1);
        setFormSaveData(initFormSaveData);
        getComposers(
          getSearchParams({
            page: page,
            sort_by: sort_by,
            sort_order: sort_order,
            rows: rows,
          })
        );
        setLoading(false);
      }
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
      setLoading(false);
    }
  }

  const handleFormChange = (data: any) => {
    if (data.path == "status") {
      if (data.value) {
        data.value = 1;
      } else {
        data.value = 0;
      }
    }
    let form = updateIn(formSaveData, [data.path], data.value);
    setFormSaveData(form);
  };

  const handleSearchFormChange = (data: any) => {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  };

  const handleDataDelete = async (composerId: number) => {
    try {
      setLoading(true);
      const response: any = await HelperServices.deleteComposer(composerId);
      if (response.status) {
        UtilityServices.showToaster({
          message: response?.data?.message,
        });
        setPage(1);
        getComposers(
          getSearchParams({
            page: page,
            sort_by: sort_by,
            sort_order: sort_order,
            rows: rows,
          })
        );
      } else {
        UtilityServices.showToaster({
          message: response?.data?.message,
        });
      }
      setLoading(false);
    } catch (error: any) {
      console.log(error);
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
      setLoading(false);
    }
  };

  function onPageChange(page: number) {
    setPage(page);
    getComposers(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getComposers(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  const handleChangeRows = (rows: any) => {
    setPage(1);
    setRows(rows);
    getComposers(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  const filterComposers = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getComposers(
        getSearchParams({
          page: page,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getComposers(
      getSearchParams({
        name: "",
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  async function init() {
    try {
      setShowCreate(
        UtilityServices.hasPermission("create-song") && showCreateForm
      );
    } catch (error: any) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
    getComposers(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }, []);

  return (
    <div
      className={`composers composer ${
        withHeader ? "composers-page" : "composers-picker"
      }`}
    >
      <Container className="container">
        {withHeader && (
          <Header className="header composer-page-header">
            <h4>{t("COMPOSERS")}</h4>
          </Header>
        )}
        <Content className="composers-content">
          {showCreate && (
            <Form formValue={formSaveData} className="entity-picker-createbar">
              <Col md={9} className="entity-picker-create-field">
                <Form.Group controlId="composer_name">
                  <Form.Control
                    name="composer_name"
                    size="sm"
                    placeholder={t("COMPOSER") + " " + t("NAME")}
                    onChange={(v: any) =>
                      handleFormChange({ path: "composer_name", value: v })
                    }
                    value={formSaveData?.composer_name}
                  />
                </Form.Group>
                <span className="text-danger">{errors["composer_name"]}</span>
              </Col>
              <Col md={3} className="entity-picker-create-actions">
                <div className="entity-picker-create-buttons">
                  <Button title={t("ADD")} onClick={addComposer}>
                    <SaveIcon />
                  </Button>
                  <Button title={t("CLEAR")} onClick={clearSaveForm}>
                    <CloseIcon />
                  </Button>
                </div>
              </Col>
            </Form>
          )}
          <div className="row entity-picker-toolbar-row composer-controls-row">
            <div className="col-md-4 entity-picker-select-col">
              {onSelectComposer && (
                <Button
                  className="button entity-picker-select-btn"
                  appearance="primary"
                  onClick={() => setSelectedComposer()}
                >
                  {t("SELECT") + " " + t("COMPOSER")}
                </Button>
              )}
            </div>
            <div className="col-md-8 mb-2 text-right composer-search-col">
              <InputGroup className="entity-picker-searchbar composer-smart-search">
                <Input
                  className="form-control"
                  placeholder={t("SMART_SEARCH_BY", {
                    entity:
                      t("COMPOSER").toLowerCase() +
                      " " +
                      t("NAME").toLowerCase(),
                  })}
                  value={smartSearchInput}
                  onChange={(val: any) => setSmartSearch(val)}
                  onKeyDown={(e: any) => filterComposers(e)}
                />
                <InputGroup.Button
                  title={t("SEARCH")}
                  onClick={(e: any) => filterComposers(e)}
                >
                  <SearchIcon />
                </InputGroup.Button>
                <InputGroup.Button
                  title={t("CLEAR")}
                  onClick={() => clearSmartSearch()}
                >
                  <CloseIcon />
                </InputGroup.Button>
              </InputGroup>
            </div>
          </div>
          <div
            className={total > 0 ? "composer-table" : "composer-empty-wrap"}
          >
            <Table
              className="entity-picker-table"
              loading={loading}
              height={400}
              fillHeight={true}
              data={data}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              sortType={sort_order}
              bordered
              cellBordered
              hover={true}
              renderEmpty={() => (
                <div className="entity-picker-empty-state composer-empty-state">
                  <div className="entity-picker-empty-icon">0</div>
                  <h4 className="entity-picker-empty-title">
                    {t("NO_ENTITY_FOUND", { entity: t("COMPOSER") })}
                  </h4>
                  <p className="entity-picker-empty-text">
                    {t("ADJUST_FILTER") +
                      " " +
                      t("OR") +
                      " " +
                      t("CREATE_NEW_ENTITY", { entity: t("COMPOSER") })}
                  </p>
                </div>
              )}
            >
          <Column width={40} align="center" fixed>
            <CustomHeaderCell>
              <div>
                {multiSelect && (
                  <Checkbox
                    inline
                    checked={checked}
                    indeterminate={indeterminate}
                    onChange={handleCheckAll}
                  />
                )}
              </div>
            </CustomHeaderCell>
            <CheckCell
              dataKey="id"
              checkedKeys={checkedKeys}
              onChange={handleCheck}
            />
          </Column>
          <Column flexGrow={1} align="left" fixed sortable>
            <CustomHeaderCell>
              {t("COMPOSER") + " " + t("NAME")}
            </CustomHeaderCell>
            <MasterEditCell
              dataKey="composer_name"
              onChange={handleDataChanges}
            />
          </Column>
          <Column flexGrow={1} align="center" sortable>
            <CustomHeaderCell>{t("STATUS")}</CustomHeaderCell>
            <MasterEditCell dataKey="status" onChange={handleDataChanges} />
          </Column>

          {showCreate ? (
            <Column flexGrow={1} align="center">
              <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
              <ActionCell
                dataKey="id"
                onClick={handleEditData}
                onDelete={(id: any) => {
                  setOpenDeleteModal(true);
                  setComposerId(id);
                }}
              />
            </Column>
          ) : (
            ""
          )}
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            total={total}
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total", "-", "limit", "|", "pager", "skip"]}
            limitOptions={appConstObj.limit_options}
            limit={rows}
            activePage={page}
            onChangePage={onPageChange}
            onChangeLimit={handleChangeRows}
          />
        </Footer>
      </Container>
      <AlertModal
        open={openNoComposerModal}
        handleClose={(e: any) => {
          if (e) {
            setOpenNoComposerModal(false);
          }
        }}
        displayText={t("PLEASE_SELECT") + " " + t("COMPOSER")}
        modalTitle={t("PLEASE_SELECT")}
        cancelButton={false}
      />
      <AlertModal
        open={openDeleteModal}
        handleClose={(e: any) => {
          if (e == true) {
            handleDataDelete(composerId);
            setOpenDeleteModal(false);
          } else {
            setOpenDeleteModal(false);
          }
        }}
        displayText={t("CONFIRM_ACTION", {
          action: t("DELETE").toLowerCase(),
          entity: t("COMPOSER").toLowerCase(),
        })}
        modalTitle={t("CONFIRM_ACTION_TITLE", {
          action: t("COMPOSER").toLowerCase(),
        })}
      />
    </div>
  );
}
export default Composers;
